import { DateTime } from "luxon";
import { getWeekStart } from "./dateUtils";
import type { DepartmentScheduleResponse, ScheduleDay, SchedulePerson, ScheduleWeek } from "./models";

const DAYS_PER_WEEK = 7;
const ISO_DATE = "yyyy-MM-dd";

/** A date as the schedule endpoints and the cache keys spell it. */
export const toIsoDate = (date: DateTime): string => date.toFormat(ISO_DATE);

/** The last day of the week starting on `weekStart`, both as ISO dates. */
export const weekEndOf = (weekStart: string): string => toIsoDate(DateTime.fromISO(weekStart).plus({ days: DAYS_PER_WEEK - 1 }));

/** The first day of every week from the one holding `from` through the one holding `to`, weeks starting on
 * `startDay` (0=Sun..6=Sat). Empty when `to` is before `from`. */
export const weekStartsBetween = (from: DateTime, to: DateTime, startDay: number): string[] => {
	const weekStarts: string[] = [];
	const lastDay = to.startOf("day");
	for (let weekStart = getWeekStart(from, startDay); weekStart <= lastDay; weekStart = weekStart.plus({ weeks: 1 })) {
		weekStarts.push(toIsoDate(weekStart));
	}
	return weekStarts;
};

/** The response as one cache entry: its days as they came, and its people by id. */
export const toScheduleWeek = (response: DepartmentScheduleResponse): ScheduleWeek => ({
	days: response.days,
	nurses: Object.fromEntries(response.nurses.map((person) => [person.id, person])),
	scheduleStartDate: response.scheduleStartDate,
	scheduleEndDate: response.scheduleEndDate,
});

/** The days of `weeks` from `from` through `to`, in date order. */
export const daysInRange = (weeks: ScheduleWeek[], from: string, to: string): ScheduleDay[] =>
	weeks
		.flatMap((week) => week.days)
		.filter((day) => {
			const date = toIsoDate(day.date);
			return date >= from && date <= to;
		})
		.sort((day, otherDay) => day.date.toMillis() - otherDay.date.toMillis());

/** Everyone in `weeks`, once. */
export const peopleOf = (weeks: ScheduleWeek[]): Record<number, SchedulePerson> => Object.assign({}, ...weeks.map((week) => week.nurses));
